import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  ChevronRight,
  Info,
  LockKeyhole,
  ShoppingCart,
  Sparkle,
  Star,
  StoreIcon,
  TriangleAlert,
  Trophy,
  WandSparkles,
} from "lucide-react";
import Amount from "utils/amount";
import {
  OptimizedScenario,
} from "../types/optimizedCart";

import {
  ReactNode,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getStoreBdData } from "utils/storeDataManager";
import type { StoreBdData } from "utils/storeDataManager";
import { isDesktopSize } from "utils/is-desktop";
import { useMediaQuery } from "@/hooks/use-media-query";
import { getTotalsFromScenario } from "utils/scenarioUtils";
import { AppDataContext } from "modules/main/context/appData";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";

const FIRST_LOGIN_UNLOCK_DELAY_MS = 700;

interface OptimizedCartResumeProps extends React.HTMLAttributes<HTMLDivElement> {
  scenario: OptimizedScenario;
  store_id?: number;
  loading?: boolean;
  validationLoading?: boolean;
  compact?: boolean;
  hideSavingsBadge?: boolean;
  createCartCallBack?: (scenarioId: number, storeId?: number) => void;
  viewDetailsCallBack?: (scenarioId: number, storeId?: number) => void;
}

export const OptimizedCartCard = forwardRef<
  HTMLDivElement,
  OptimizedCartResumeProps
>(({ scenario, ...props }, ref) => {
  const isDesktop = useMediaQuery(isDesktopSize);
  const { isLogged, isNewUser} = useAuth();
  const [unlockAnimationId, setUnlockAnimationId] = useState(0);
  const [showFirstLoginLockPreview, setShowFirstLoginLockPreview] =
    useState(false);
  const previousLoggedState = useRef(isLogged);
  const { storeList } = useContext(AppDataContext);
  const bestStore = scenario.scenarioType === "UNIQUE_STORE";
  const theoreticalValues = scenario.scenarioType === "UNIQUE_STORE";
  const {
    totalPurchase,
    deliveryTotal,
    productsCount,
    cheapestStoreId,
    productsCountUnmet,
    totalUnmetCost,
    theoreticalDelivery,
  } = getTotalsFromScenario(scenario, bestStore, theoreticalValues);
  const totalSaving = scenario.total_saved;

  const hasUnmetProducts = productsCountUnmet > 0;

  const optionName = hasUnmetProducts
    ? scenario.scenarioType === "SAVINGS"
      ? "Máximo Ahorro - Conseguimos casi todo"
      : scenario.scenarioType === "BALANCED"
        ? "Balanceado Con casi todos los productos"
        : scenario.scenarioType === "CONVENIENCE"
          ? "Casi todo en un solo lugar"
          : scenario.scenarioType === "UNIQUE_STORE"
            ? "Casi todo en un solo lugar"
            : scenario.scenarioType === "COMPLETED"
              ? "Pedido parcial"
              : "Desconocido"
    : scenario.scenarioType === "SAVINGS"
      ? "Máximo Ahorro"
      : scenario.scenarioType === "BALANCED"
        ? "Balanceado"
        : scenario.scenarioType === "CONVENIENCE"
          ? "Todo en un solo lugar"
          : scenario.scenarioType === "UNIQUE_STORE"
            ? "Todo en un solo lugar"
            : scenario.scenarioType === "COMPLETED"
              ? "Pedido Completo"
              : "Desconocido";

  const idsFromTotals = (scenario.total_per_store_jsons ?? []).map(
    (item) => item.store_id,
  );
  const idsFromResume = (scenario.store_resume ?? []).map((item) => item.id);
  const storeIds = Array.from(new Set([...idsFromTotals, ...idsFromResume]));
  const stores = storeIds.map((id) => getStoreBdData(id, storeList));
  const visibleStores = stores.slice(0, 4);
  const storesCount = scenario.store_resume?.length ?? 0;

  const storeTotals = scenario.total_per_store_jsons ?? [];
  const fallbackStoreId =
    storeTotals[0]?.store_id ?? scenario.store_resume[0]?.id;
  const scenarioStoreId =
    scenario.scenarioType === "UNIQUE_STORE"
      ? cheapestStoreId
      : fallbackStoreId;
  const scenarioStore = getStoreBdData(scenarioStoreId ?? 0, storeList);

  useEffect(() => {
    if (!previousLoggedState.current && isLogged) {
      setUnlockAnimationId((current) => current + 1);
    }

    previousLoggedState.current = isLogged;
  }, [isLogged]);

  useEffect(() => {
    if (!isLogged || !isNewUser) {
      return;
    }

    setShowFirstLoginLockPreview(true);

    const timeoutId = window.setTimeout(() => {
      setShowFirstLoginLockPreview(false);
      setUnlockAnimationId((current) => current + 1);
    }, FIRST_LOGIN_UNLOCK_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
      setShowFirstLoginLockPreview(false);
    };
  }, [isLogged, isNewUser]);

  const effectiveIsLogged = isLogged && !showFirstLoginLockPreview;
  const canCreateCart = effectiveIsLogged; // && isPremiumUser;

  return scenario.reccomended ? (
    <RecommendedScenarioCard
      scenario={scenario}
      optionName={optionName}
      isDesktop={isDesktop}
      totalProducts={productsCount}
      totalMissedProducts={productsCountUnmet}
      totalMissedProductsAmount={totalUnmetCost}
      storesCount={storesCount}
      totalPurchase={totalPurchase}
      deliveryTotal={deliveryTotal}
      theoreticalDelivery={theoreticalDelivery}
      totalSaving={totalSaving}
      stores={stores}
      visibleStores={visibleStores}
      scenarioStore={scenarioStore}
      createCartCallBack={props.createCartCallBack}
      viewDetailsCallBack={props.viewDetailsCallBack}
      compact={props.compact}
      isLogged={effectiveIsLogged}
      canCreateCart={canCreateCart}
      unlockAnimationId={unlockAnimationId}
      validationLoading={props.validationLoading}
      hideSavingsBadge={props.hideSavingsBadge}
    />
  ) : (
    <ScenarioCard
      scenario={scenario}
      optionName={optionName}
      isDesktop={isDesktop}
      totalProducts={productsCount}
      totalMissedProducts={productsCountUnmet}
      totalMissedProductsAmount={totalUnmetCost}
      storesCount={storesCount}
      totalPurchase={totalPurchase}
      deliveryTotal={deliveryTotal}
      theoreticalDelivery={theoreticalDelivery}
      totalSaving={totalSaving}
      stores={stores}
      visibleStores={visibleStores}
      scenarioStore={scenarioStore}
      createCartCallBack={props.createCartCallBack}
      viewDetailsCallBack={props.viewDetailsCallBack}
      compact={props.compact}
      isLogged={effectiveIsLogged}
      canCreateCart={canCreateCart}
      unlockAnimationId={unlockAnimationId}
      validationLoading={props.validationLoading}
    />
  );
});

interface ScenarioCardProps {
  scenario: OptimizedScenario;
  optionName: string;
}

interface ScenarioSharedProps extends ScenarioCardProps {
  isLogged: boolean;
  canCreateCart: boolean;
  unlockAnimationId: number;
  validationLoading?: boolean;
  isDesktop: boolean;
  totalProducts: number;
  theoreticalDelivery: number;
  totalMissedProducts: number;
  totalMissedProductsAmount: number;
  storesCount: number;
  totalPurchase: number;
  deliveryTotal: number;
  totalSaving: number;
  compact?: boolean;
  hideSavingsBadge?: boolean;
  stores: StoreBdData[];
  visibleStores: StoreBdData[];
  scenarioStore: StoreBdData;
  createCartCallBack?: (scenarioId: number, storeId?: number) => void;
  viewDetailsCallBack?: (scenarioId: number, storeId?: number) => void;
}

interface AuthActionIconProps {
  isLogged: boolean;
  unlockAnimationId: number;
  unlockedIcon: ReactNode;
  lockClassName?: string;
}

const AuthActionIcon = ({
  isLogged,
  unlockAnimationId,
  unlockedIcon,
  lockClassName,
}: AuthActionIconProps) => {
  return (
    <span className="inline-flex items-center justify-center">
      <AnimatePresence initial={false} mode="wait">
        {isLogged ? (
          <motion.span
            key={`unlock-${unlockAnimationId}`}
            initial={{ opacity: 0, scale: 0.62, rotate: -18 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.86 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className="inline-flex items-center justify-center"
          >
            {unlockedIcon}
          </motion.span>
        ) : (
          <motion.span
            key="lock"
            initial={{ opacity: 1, scale: 1, rotate: 0 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.55, rotate: -18, y: -8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="inline-flex items-center justify-center"
          >
            <LockKeyhole className={cn("w-4 h-4", lockClassName)} />
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
};

const getInitials = (name?: string) => {
  if (!name) return "--";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const formatStoreNames = (stores: Array<StoreBdData>) => {
  const names = stores.map((store) => store?.name).filter(Boolean) as string[];
  if (names.length === 0) return "Sin tiendas";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} y ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} y ${names[names.length - 1]}`;
};

const RecommendedScenarioCard = ({
  optionName,
  isDesktop,
  totalProducts,
  totalMissedProducts,
  storesCount,
  totalPurchase,
  deliveryTotal,
  totalSaving,
  stores,
  visibleStores,
  isLogged,
  canCreateCart,
  unlockAnimationId,
  theoreticalDelivery,
  ...props
}: ScenarioSharedProps) => {
  const [isSavingTooltipOpen, setIsSavingTooltipOpen] = useState(false);
  const storeCartId =
    props.scenario.scenarioType === "UNIQUE_STORE" ||
    props.scenario.number_stores === 1
      ? props.scenario.store_resume[0]?.id
      : undefined;
  const storeId =
    props.scenario.scenarioType === "UNIQUE_STORE" ||
    props.scenario.number_stores === 1
      ? props.scenario.store_resume?.[0]?.id
      : undefined;
  const totalAmount = (totalPurchase ?? 0) + (deliveryTotal ?? 0);

  /**
   * Handles a card click/tap on mobile, navigating to scenario details
   * when the tap did not originate from an interactive element.
   */
  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDesktop || !props.viewDetailsCallBack) return;
    const target = e.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest("a") ||
      target.closest('[role="button"]')
    )
      return;
    props.viewDetailsCallBack(props.scenario.order_id, storeId);
  };

  const pricingDetail = (
    <div className="flex flex-col w-full gap-1">
      <div className="flex w-full justify-between gap-2">
        <span className="text-muted-foreground text-sm font-semibold">
          Productos ({totalProducts}):
        </span>
        <span className="text-sm font-semibold">
          <Amount
            className="ml-0 font-semibold"
            amount={totalPurchase}
            currency="CLP"
            locale="es-CL"
          />
        </span>
      </div>
      {totalMissedProducts > 0 && (
        <div className="flex w-full justify-between gap-2">
          <span className="text-destructive text-xs font-semibold">
            No encontrados ({totalMissedProducts}):
          </span>
          <span className="text-xs text-muted-foreground">Agotados</span>
        </div>
      )}
      <div className="flex w-full justify-between gap-2">
        <span className="text-muted-foreground text-sm font-semibold">
          Despacho:
        </span>
        <span className="text-sm">
          <Amount
            className="ml-0 font-semibold"
            amount={deliveryTotal ?? 0}
            currency="CLP"
            locale="es-CL"
          />
        </span>
      </div>
      <div className="flex w-full justify-between gap-2 items-center">
        <span className="text-sm font-extrabold">Total</span>
        <span className="text-sm">
          <Amount
            className="ml-0 font-extrabold"
            amount={totalAmount}
            currency="CLP"
            locale="es-CL"
          />
        </span>
      </div>
    </div>
  );
  return (
    <>
      <Card
        className={cn(
          "w-[19rem] min-w-[19rem]  max-h-96 flex-1 border-0 shadow-none text-sm p-[2px] recommended-ai-border",
          {
            "bg-[linear-gradient(90deg,#F9802D_0%,#F9802D_36%,#2F2D2E_48%,#5CA273_62%,#5CA273_100%)] rounded-2xl": true,
          },
          { "cursor-pointer hover:scale-[1.02] transition-transform": !true },
          { "w-full": !isDesktop },
          {
            "w-full": isDesktop,
          },
        )}
      >
        <div
          className={cn(
            "bg-white rounded-[calc(1rem-2px)] h-full py-3 px-0 flex flex-col relative min-h-32",
            {
              "cursor-pointer": !isDesktop && !!props.viewDetailsCallBack,
            },
          )}
          onClick={handleCardClick}
        >
          <CardHeader className={cn({"px-4": !isDesktop})}>
            <div className="flex w-full justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold">
                <Trophy className="w-4 h-4 best-price" /> {optionName}
              </div>
              <div>
                <Badge className="text-xxs">
                  <>
                    <Star
                      fill="currentColor"
                      className="w-4 h-4 text-yellow-400 fill-yellow-400"
                    />
                    <span className="text-white">Recomendado</span>
                  </>
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className={cn({"px-4": !isDesktop})}>
            <div className="flex flex-col w-full gap-3 justify-evenly mt-2">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="recommended-price-detail" className="border-b-0">
                  <AccordionTrigger className="p-0 hover:no-underline [&>svg]:ml-2">
                    <div className="flex w-full justify-items-start gap-4 text-left">
                      <div className="flex -space-x-6 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background *:data-[slot=avatar]:grayscale">
                        {visibleStores.map((store, index) => (
                          <Avatar
                            key={`${store?.name ?? "store"}-${index}`}
                            className="sm:flex h-9 w-9"
                          >
                            <AvatarImage
                              src={store?.logo_url}
                              alt={store?.name ?? "Tienda"}
                            />
                            <AvatarFallback>
                              {getInitials(store?.name)}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                      </div>

                      <div className="flex w-full items-start justify-between gap-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-extrabold">
                            {formatStoreNames(stores)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            <span className="font-semibold text-foreground">
                              {totalProducts}
                            </span>{" "}
                            productos en{" "}
                            <span className="font-semibold text-foreground">
                              {storesCount}
                            </span>{" "}
                            {storesCount === 1 ? "tienda" : "tiendas"}
                          </span>
                        </div>
                        <div className="flex shrink-0 flex-col items-end text-right">
                          <span className="text-xxs font-semibold uppercase tracking-wide text-muted-foreground">
                            Total
                          </span>
                          <Amount
                            className="ml-0 text-lg font-extrabold leading-none"
                            amount={totalAmount}
                            currency="CLP"
                            locale="es-CL"
                          />
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-1 pt-2">
                    {pricingDetail}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <div className=" flex w-full justify-between items-center">
                {totalSaving > 0 && !props.hideSavingsBadge ? (
                  <div>
                    <Tooltip
                      open={isDesktop ? undefined : isSavingTooltipOpen}
                      onOpenChange={(nextOpen) => {
                        if (!isDesktop) {
                          setIsSavingTooltipOpen(nextOpen);
                        }
                      }}
                    >
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex"
                          onClick={(event) => {
                            if (!isDesktop) {
                              event.preventDefault();
                              setIsSavingTooltipOpen((prev) => !prev);
                            }
                          }}
                        >
                          <Badge className="text-sm bg-green-600">
                            {" "}
                            Ahorras
                            <Amount
                              className="ml-0 font-bold text-white"
                              amount={totalSaving ?? 0}
                              currency="CLP"
                              locale="es-CL"
                            />
                            <Info className="w-3 h-3 ml-1 text-white" />
                          </Badge>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-60 whitespace-normal break-words">
                        <p>
                          Calculado sobre el costo promedio de conseguir tu
                          carrito en una sola tienda
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    {props.scenario.reason && (
                      <div className="mt-1 px-2 text-muted-foreground text-xxs">
                        <Sparkle
                          size={10}
                          className="inline align-middle mr-0.5"
                        />
                        {props.scenario.reason}
                      </div>
                    )}
                  </div>
                ) : (
                  <div></div>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {props.viewDetailsCallBack && (
                    <Button
                      variant={"default"}
                      onClick={() =>
                        props.viewDetailsCallBack &&
                        props.viewDetailsCallBack(
                          props.scenario.order_id,
                          storeId,
                        )
                      }
                    >
                      <span className="text-white">Ver detalles</span>
                      <AuthActionIcon
                        isLogged={isLogged}
                        unlockAnimationId={unlockAnimationId}
                        unlockedIcon={
                          <ChevronRight className="h-4 w-4 text-white" />
                        }
                      />
                    </Button>
                  )}
                  {isDesktop && (
                    <Button
                      onClick={() => {
                        if (isLogged && !canCreateCart) {
                          toast.info("Acceso exclusivo para Carriapp Plus", {
                            description:
                              "Actualiza tu plan para crear carritos desde esta opción.",
                          });
                          return;
                        }
                        props.createCartCallBack?.(
                          props.scenario.order_id,
                          storeCartId,
                        );
                      }}
                    >
                      <AuthActionIcon
                        isLogged={canCreateCart}
                        unlockAnimationId={unlockAnimationId}
                        unlockedIcon={<WandSparkles className="w-4 h-4 mr-2" />}
                        lockClassName="mr-2"
                      />
                      Crea tu Carrito
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </div>
      </Card>
    </>
  );
};

const ScenarioCard = ({
  optionName,
  scenarioStore,
  totalProducts,
  totalMissedProducts,
  storesCount,
  totalPurchase,
  deliveryTotal,
  isLogged,
  canCreateCart,
  unlockAnimationId,
  visibleStores,
  stores,
  ...props
}: ScenarioSharedProps) => {
  const totalProductsEstimate = totalPurchase + props.totalMissedProductsAmount;
  const completedPercentage =
    totalProductsEstimate > 0
      ? totalPurchase / totalProductsEstimate
      : 0;

  const isDesktop = useMediaQuery(isDesktopSize);
  const storeCartId =
    props.scenario.scenarioType === "UNIQUE_STORE" ||
    props.scenario.scenarioType === "CONVENIENCE"
      ? props.scenario.store_resume[0]?.id
      : undefined;
  const storeId =
    props.scenario.scenarioType === "UNIQUE_STORE" ||
    props.scenario.scenarioType === "CONVENIENCE"
      ? props.scenario.store_resume?.[0]?.id
      : undefined;
  const estimatedTotal =
    totalPurchase +
    (deliveryTotal ?? 0) +
    (totalMissedProducts > 0
      ? (props.totalMissedProductsAmount ?? 0) +
        (props.theoreticalDelivery ?? 0)
      : 0);

  /**
   * Handles a card click/tap on mobile, navigating to scenario details
   * when the tap did not originate from an interactive element.
   */
  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDesktop || !props.viewDetailsCallBack) return;
    const target = e.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest("a") ||
      target.closest('[role="button"]')
    )
      return;
    props.viewDetailsCallBack(props.scenario.order_id, storeId);
  };

  const PriceDetail = (
    <div className="flex flex-col w-full gap-1">
      <div className="flex w-full justify-between gap-2">
        <span className="text-muted-foreground text-xs font-semibold">
          Productos ({totalProducts}):
        </span>
        <span className="text-xs font-semibold">
          <Amount
            className="ml-0 font-semibold"
            amount={totalPurchase}
            currency="CLP"
            locale="es-CL"
          />
        </span>
      </div>
      <div className="flex w-full justify-between gap-2">
        <span className="text-muted-foreground text-xs font-semibold">
          Despacho:
        </span>
        <span className="text-xs">
          <Amount
            className="ml-0 font-semibold"
            amount={deliveryTotal}
            currency="CLP"
            locale="es-CL"
          />
        </span>
      </div>
      <div className="flex w-full justify-between gap-2 items-center">
        <span className="text-xs font-extrabold">
          Total {totalMissedProducts > 0 && "parcial"}
        </span>
        <span className="text-xs">
          <Amount
            className="ml-0 font-extrabold"
            amount={(totalPurchase ?? 0) + (deliveryTotal ?? 0)}
            currency="CLP"
            locale="es-CL"
          />
        </span>
      </div>
      {totalMissedProducts > 0 && (
        <>
          <Separator className="my-2" />
          <span className="text-xxs mr-auto font-semibold">
            Para completar tu compra:
          </span>

          <div className="flex w-full justify-between gap-2">
            <span className="text-destructive text-xxs font-semibold">
              Faltan {totalMissedProducts} productos:
            </span>
            <span className="text-xxs text-muted-foreground ">
              <Amount
                className="ml-0 font-semibold"
                amount={props.totalMissedProductsAmount}
                currency="CLP"
                locale="es-CL"
              />
            </span>
          </div>
          {props.theoreticalDelivery && (
            <div className="flex w-full justify-between gap-2">
              <span className="text-destructive text-xxs font-semibold">
                Despacho en otra tienda:
              </span>
              <span className="text-xxs text-muted-foreground ">
                <Amount
                  className="ml-0 font-semibold"
                  amount={props.theoreticalDelivery ?? 0}
                  currency="CLP"
                  locale="es-CL"
                />
              </span>
            </div>
          )}
          <div className="flex w-full justify-between gap-2 items-center">
            <span className="text-xxs font-extrabold">Total estimado</span>
            <span className="text-xxs">
              <Amount
                className="ml-0 font-extrabold"
                amount={estimatedTotal}
                currency="CLP"
                locale="es-CL"
              />
            </span>
          </div>
        </>
      )}
    </div>
  );
  return (
    <>
      <Card
        className={cn(
          "gap-2 py-4",
          { "w-full": !isDesktop },
          {
            "w-full": isDesktop,
            "cursor-pointer": !isDesktop && !!props.viewDetailsCallBack,
          },
        )}
        onClick={handleCardClick}
      >
        <CardHeader className={cn({"px-4": !isDesktop})}>
          <div className="flex w-full justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold">
              {totalMissedProducts > 0 ? (
                <>
                  <TriangleAlert className="w-4 h-4 text-yellow-500" />
                  Compra parcial
                  <Amount
                    className="ml-0 font-extrabold"
                    amount={completedPercentage}
                    formatStyle="percent"
                    decimalPlaces={0}
                    locale="es-CL"
                  />{" "}
                  de tu lista
                </>
              ) : (
                <>
                  <StoreIcon className="w-4 h-4 best-price" /> {optionName}
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className={cn({"px-4": !isDesktop})}>
          <div className="flex flex-col w-full gap-3 justify-evenly">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="price-detail" className="border-b-0">
                <AccordionTrigger className="p-0 hover:no-underline [&>svg]:ml-2">
                  <div className="flex w-full justify-items-start gap-4 text-left">
                    <div className="flex -space-x-6 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background *:data-[slot=avatar]:grayscale">
                      {visibleStores.map((store, index) => (
                        <Avatar
                          key={`${store?.name ?? "store"}-${index}`}
                          className="sm:flex h-9 w-9"
                        >
                          <AvatarImage
                            src={store?.logo_url}
                            alt={store?.name ?? "Tienda"}
                          />
                          <AvatarFallback>
                            {getInitials(store?.name)}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                    </div>
                    <div className="flex w-full items-start justify-between gap-3">
                      <div className="flex flex-col">
                        <span className="text-sm font-extrabold">
                          {formatStoreNames(stores)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          <span className="font-semibold text-foreground">
                            {totalProducts}
                          </span>{" "}
                          productos en{" "}
                          <span className="font-semibold text-foreground">
                            {storesCount}
                          </span>{" "}
                          {storesCount === 1 ? "tienda" : "tiendas"}
                        </span>
                      </div>
                      <div className="flex shrink-0 flex-col items-end text-right">
                        <span className="text-xxs font-semibold uppercase tracking-wide text-muted-foreground">
                          Total estimado
                        </span>
                        <Amount
                          className="ml-0 text-lg font-extrabold leading-none"
                          amount={estimatedTotal}
                          currency="CLP"
                          locale="es-CL"
                        />
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-1 pt-2">
                  {PriceDetail}
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="flex w-full flex-wrap justify-between gap-2 items-center">
              {props.viewDetailsCallBack && (
                <div>
                  <Button
                    variant={"secondary"}
                    size={"xs"}
                    className="text-xxs font-semibold"
                    onClick={() =>
                      props.viewDetailsCallBack &&
                      props.viewDetailsCallBack(
                        props.scenario.order_id,
                        storeId,
                      )
                    }
                  >
                    Ver detalles
                    <AuthActionIcon
                      isLogged={isLogged}
                      unlockAnimationId={unlockAnimationId}
                      unlockedIcon={<ChevronRight className="h-4 w-4" />}
                    />
                  </Button>
                </div>
              )}
              <div className="ml-auto">
                <Button
                  variant={"secondary"}
                  size={"xs"}
                  className="text-xxs font-semibold"
                  onClick={() => {
                    if (isLogged && !canCreateCart) {
                      toast.info("Acceso exclusivo para Carriapp Plus", {
                        description:
                          "Actualiza tu plan para crear carritos desde esta opción.",
                      });
                      return;
                    }
                    props.createCartCallBack?.(
                      props.scenario.order_id,
                      storeCartId,
                    );
                  }}
                >
                  <AuthActionIcon
                    isLogged={canCreateCart}
                    unlockAnimationId={unlockAnimationId}
                    unlockedIcon={<ShoppingCart className="w-4 h-4 mr-1" />}
                    lockClassName="mr-1"
                  />

                  {totalMissedProducts > 0
                    ? "Armar carrito parcial"
                    : "Elegir esta opción"}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
